import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Users, Crown, Wallet, DollarSign, ShieldCheck, Check, X, Inbox, LayoutGrid, Mail, RotateCcw, Radio, BarChart3, FlaskConical, Copy, Star } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => { try { return new Date(s).toLocaleString(); } catch { return s; } };

function Stat({ Icon, label, value, accent }) {
  return (
    <div className="bg-[#0d1730] border border-white/10 rounded-2xl p-5">
      <Icon className={`w-5 h-5 ${accent}`} />
      <p className="text-[11px] uppercase tracking-wider text-slate-500 mt-3">{label}</p>
      <p className="text-2xl font-bold text-white mt-1 font-mono">{value}</p>
    </div>
  );
}

function SupportInbox() {
  const [tickets, setTickets] = useState([]);
  const [filter, setFilter] = useState("open");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/support").then((r) => setTickets(r.data.tickets || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const setStatus = async (id, status) => {
    setTickets((ts) => ts.map((t) => (t.id === id ? { ...t, status } : t)));
    try {
      await api.patch(`/admin/support/${id}`, { status });
      toast.success(status === "resolved" ? "Marked resolved" : "Reopened");
    } catch {
      toast.error("Could not update");
      load();
    }
  };

  const counts = {
    all: tickets.length,
    open: tickets.filter((t) => t.status !== "resolved").length,
    resolved: tickets.filter((t) => t.status === "resolved").length,
  };
  const shown = tickets.filter((t) => filter === "all" || (filter === "open" ? t.status !== "resolved" : t.status === "resolved"));

  return (
    <div data-testid="admin-support-inbox">
      <div className="flex items-center gap-2 mb-5">
        {["open", "resolved", "all"].map((f) => (
          <button
            key={f}
            data-testid={`inbox-filter-${f}`}
            onClick={() => setFilter(f)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors capitalize ${filter === f ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" : "border-white/10 text-slate-400 hover:text-slate-200"}`}
          >
            {f} ({counts[f]})
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-slate-500 text-sm">Loading…</p>
      ) : shown.length === 0 ? (
        <div data-testid="inbox-empty" className="text-center py-16 text-slate-500">
          <Inbox className="w-8 h-8 mx-auto mb-3 opacity-40" />
          <p className="text-sm">No {filter === "all" ? "" : filter} tickets.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {shown.map((t) => (
            <div key={t.id} data-testid={`inbox-ticket-${t.id}`} className="bg-[#0d1730] border border-white/10 rounded-2xl p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/5 text-slate-300">{t.subject || "General"}</span>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${t.status === "resolved" ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}>{t.status || "open"}</span>
                  </div>
                  <p className="text-white font-medium mt-2">{t.name || "Anonymous"}</p>
                  <a href={`mailto:${t.email}?subject=Re: ${encodeURIComponent(t.subject || "your message")}`} data-testid={`inbox-reply-${t.id}`} className="text-xs font-mono text-sky-400 hover:underline inline-flex items-center gap-1">
                    <Mail className="w-3 h-3" /> {t.email}
                  </a>
                  <p className="text-sm text-slate-300 mt-3 whitespace-pre-wrap break-words">{t.message}</p>
                  <p className="text-[11px] text-slate-600 mt-3">{fmtDate(t.created_at)}</p>
                </div>
                <div className="shrink-0">
                  {t.status === "resolved" ? (
                    <button data-testid={`inbox-reopen-${t.id}`} onClick={() => setStatus(t.id, "open")} className="text-xs px-3 py-2 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5 transition-colors inline-flex items-center gap-1.5">
                      <RotateCcw className="w-3.5 h-3.5" /> Reopen
                    </button>
                  ) : (
                    <button data-testid={`inbox-resolve-${t.id}`} onClick={() => setStatus(t.id, "resolved")} className="text-xs px-3 py-2 rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 transition-colors inline-flex items-center gap-1.5">
                      <Check className="w-3.5 h-3.5" /> Resolve
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BetaProgram() {
  const [testers, setTesters] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState("");
  const [emailInvite, setEmailInvite] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = () => {
    api.get("/admin/beta").then((r) => setTesters(r.data.testers || [])).catch(() => {});
    api.get("/admin/beta/feedback").then((r) => setFeedback(r.data.feedback || [])).catch(() => {});
  };
  useEffect(load, []);

  const copy = (l) => { navigator.clipboard?.writeText(l).then(() => toast.success("Link copied")).catch(() => window.prompt("Copy link:", l)); };

  const invite = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return toast.error("Add a name and email");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/beta/invite", { name, email, origin_url: window.location.origin, send_email: emailInvite });
      setLink(data.invite_link || "");
      copy(data.invite_link || "");
      if (emailInvite) {
        if (data.email_status === "sent") toast.success("Invite emailed to the tester");
        else toast.error("Couldn't send the email — link copied instead");
      }
      setName(""); setEmail(""); load();
    } catch { toast.error("Could not create invite"); }
    finally { setBusy(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this beta tester's access?")) return;
    try { await api.post(`/admin/beta/${id}/revoke`); toast.success("Revoked"); load(); } catch { toast.error("Failed"); }
  };
  const sendEmail = async (id) => {
    try {
      await api.post(`/admin/beta/${id}/send-email`, { origin_url: window.location.origin });
      toast.success("Invite emailed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send email"); }
  };
  const emailAll = async () => {
    const pending = testers.filter((t) => (t.status === "invited" || t.status === "active") && !t.email_sent).length;
    if (pending === 0) return toast.info("No pending invites to email");
    if (!window.confirm(`Email ${pending} pending invite(s) now?`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/beta/send-all", { origin_url: window.location.origin });
      toast.success(`Emailed ${data.sent} · ${data.failed} failed · ${data.skipped} already sent`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send emails"); }
    finally { setBulkBusy(false); }
  };
  const STCLS = { invited: "bg-sky-500/15 text-sky-300", active: "bg-emerald-500/15 text-emerald-300", expired: "bg-amber-500/15 text-amber-300", revoked: "bg-rose-500/15 text-rose-300" };

  return (
    <div className="mt-8 space-y-8" data-testid="admin-beta">
      <form onSubmit={invite} className="bg-[#0d1730] border border-white/10 rounded-2xl p-5">
        <p className="text-sm font-semibold text-white mb-3">Invite a beta tester</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <input data-testid="beta-invite-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" className="h-11 px-3 rounded-lg bg-[#0a1224] border border-white/10 text-sm text-white outline-none focus:border-emerald-500/50" />
          <input data-testid="beta-invite-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="h-11 px-3 rounded-lg bg-[#0a1224] border border-white/10 text-sm text-white outline-none focus:border-emerald-500/50" />
          <button data-testid="beta-invite-submit" disabled={busy} className="h-11 rounded-lg bg-emerald-500 text-[#04140c] font-semibold text-sm hover:bg-emerald-400 disabled:opacity-50">{busy ? "Creating…" : "Create invite link"}</button>
        </div>
        {link && (
          <div className="mt-3 flex items-center gap-2 bg-[#0a1224] border border-emerald-500/30 rounded-lg p-2.5">
            <span data-testid="beta-invite-link" className="text-xs font-mono text-emerald-300 truncate flex-1">{link}</span>
            <button data-testid="beta-copy-link" type="button" onClick={() => copy(link)} className="text-xs px-2 py-1 rounded bg-white/5 text-slate-200 hover:bg-white/10 inline-flex items-center gap-1"><Copy className="w-3 h-3" /> Copy</button>
          </div>
        )}
        <label data-testid="beta-invite-email-toggle" className="mt-3 flex items-center gap-2 cursor-pointer select-none w-fit">
          <input type="checkbox" data-testid="beta-invite-email-checkbox" checked={emailInvite} onChange={(e) => setEmailInvite(e.target.checked)} className="w-4 h-4 accent-emerald-500" />
          <span className="text-xs text-slate-300">Email the invite automatically <span className="text-slate-500">(sends the link + welcome note)</span></span>
        </label>
        <p className="text-[11px] text-slate-500 mt-2">{emailInvite ? "The tester gets an email with their invite link and a short welcome." : "No email is sent — copy the link and share it."} Testers get a 30-day full-access trial; email-only nudges fire in the final 7 days, then access auto-revokes on day 30.</p>
      </form>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-white">Beta testers ({testers.length})</h3>
          <button data-testid="beta-email-all" onClick={emailAll} disabled={bulkBusy} className="text-xs px-3 h-8 rounded-lg bg-sky-500/15 text-sky-300 hover:bg-sky-500/25 disabled:opacity-50 inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {bulkBusy ? "Sending…" : "Email all pending"}</button>
        </div>
        <div className="overflow-x-auto rounded-2xl border border-white/10">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-slate-400 text-left text-xs uppercase tracking-wider">
              <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Days left</th><th className="px-4 py-3"></th></tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {testers.length === 0 ? <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">No beta testers yet.</td></tr> : testers.map((t) => (
                <tr key={t.id} data-testid={`beta-row-${t.id}`} className="hover:bg-white/[0.03]">
                  <td className="px-4 py-3 text-slate-200">{t.name}</td>
                  <td className="px-4 py-3 text-slate-400 font-mono text-xs">{t.email}</td>
                  <td className="px-4 py-3"><span className={`text-[11px] px-2 py-0.5 rounded-full ${STCLS[t.status] || "bg-white/5 text-slate-400"}`}>{t.status}</span>{t.email_sent && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 inline-flex items-center gap-0.5"><Mail className="w-2.5 h-2.5" /> emailed</span>}</td>
                  <td className="px-4 py-3 text-slate-300">{t.days_left != null ? `${t.days_left}d` : "—"}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {t.status === "invited" && t.token && <button type="button" onClick={() => copy(`${window.location.origin}/beta/${t.token}`)} className="text-xs text-emerald-300 hover:underline mr-3">Copy link</button>}
                    {(t.status === "invited" || t.status === "active") && <button data-testid={`beta-send-email-${t.id}`} type="button" onClick={() => sendEmail(t.id)} className="text-xs text-sky-300 hover:underline mr-3">{t.email_sent ? "Resend email" : "Email invite"}</button>}
                    {(t.status === "invited" || t.status === "active") && <button data-testid={`beta-revoke-${t.id}`} type="button" onClick={() => revoke(t.id)} className="text-xs text-rose-400 hover:underline">Revoke</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-white mb-3">Tester feedback ({feedback.length})</h3>
        <div className="space-y-3" data-testid="admin-beta-feedback">
          {feedback.length === 0 ? <p className="text-sm text-slate-500">No feedback yet.</p> : feedback.map((f) => (
            <div key={f.id} className="bg-[#0d1730] border border-white/10 rounded-2xl p-4">
              <div className="flex items-center justify-between">
                <p className="text-slate-200 text-sm font-medium">{f.name || f.email}</p>
                <div className="flex items-center gap-0.5">{[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`w-3.5 h-3.5 ${n <= f.rating ? "fill-amber-400 text-amber-400" : "text-slate-600"}`} />)}</div>
              </div>
              {f.comment && <p className="text-sm text-slate-400 mt-2 whitespace-pre-wrap">{f.comment}</p>}
              <p className="text-[11px] text-slate-600 mt-2">{fmtDate(f.created_at)}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function AdminPortal() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (user && user.role !== "admin") { navigate("/"); return; }
    api.get("/admin/overview").then((r) => setOverview(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data.users)).catch(() => {});
    api.get("/admin/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  }, [user, navigate]);

  const stats = overview?.stats;

  return (
    <div className="min-h-screen bg-[#070d1c] text-slate-100">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <button data-testid="admin-back" onClick={() => navigate("/")} className="flex items-center gap-2 text-sm text-slate-400 hover:text-emerald-300 transition-colors mb-6">
          <ArrowLeft className="w-4 h-4" /> Dashboard
        </button>
        <h1 className="text-3xl font-bold tracking-tight text-white flex items-center gap-2" data-testid="admin-portal">
          <ShieldCheck className="w-7 h-7 text-emerald-400" /> Admin Portal
        </h1>
        <p className="text-slate-400 mt-1 text-sm">Every price point, subscriber, and support ticket at a glance.</p>

        <div className="flex items-center gap-2 mt-6 border-b border-white/10">
          <button data-testid="admin-tab-overview" onClick={() => setTab("overview")} className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors inline-flex items-center gap-2 ${tab === "overview" ? "border-emerald-400 text-white" : "border-transparent text-slate-400 hover:text-slate-200"}`}>
            <LayoutGrid className="w-4 h-4" /> Overview
          </button>
          <button data-testid="admin-tab-support" onClick={() => setTab("support")} className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors inline-flex items-center gap-2 ${tab === "support" ? "border-emerald-400 text-white" : "border-transparent text-slate-400 hover:text-slate-200"}`}>
            <Inbox className="w-4 h-4" /> Support Inbox
          </button>
          <button data-testid="admin-tab-beta" onClick={() => setTab("beta")} className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors inline-flex items-center gap-2 ${tab === "beta" ? "border-emerald-400 text-white" : "border-transparent text-slate-400 hover:text-slate-200"}`}>
            <FlaskConical className="w-4 h-4" /> Beta Program
          </button>
        </div>

        {tab === "overview" ? (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
              <Stat Icon={Users} label="Total users" value={stats?.total_users ?? "—"} accent="text-sky-400" />
              <Stat Icon={Wallet} label="Individual" value={stats?.snapbudget_premium ?? "—"} accent="text-emerald-400" />
              <Stat Icon={Radio} label="Business" value={stats?.business_premium ?? "—"} accent="text-sky-300" />
              <Stat Icon={DollarSign} label="Revenue (paid)" value={stats ? money(stats.revenue) : "—"} accent="text-amber-400" />
            </div>

            {/* Product usage analytics */}
            <h2 className="text-xl font-bold text-white mt-12 mb-4 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-emerald-400" /> Product usage</h2>
            <div className="bg-[#0d1730] border border-white/10 rounded-2xl p-5" data-testid="admin-analytics">
              <div className="flex flex-wrap gap-8 mb-5">
                <div><p className="text-[11px] uppercase tracking-wider text-slate-500">Total events</p><p className="text-2xl font-bold text-white font-mono mt-1">{analytics?.total ?? "—"}</p></div>
                <div><p className="text-[11px] uppercase tracking-wider text-slate-500">Last 7 days</p><p className="text-2xl font-bold text-emerald-400 font-mono mt-1">{analytics?.recent_7d ?? "—"}</p></div>
              </div>
              {analytics?.by_event?.length ? (
                <div className="space-y-2">
                  {analytics.by_event.map((e) => (
                    <div key={e.event} data-testid={`analytics-row-${e.event}`} className="flex items-center gap-3">
                      <span className="text-xs font-mono text-slate-300 w-48 truncate">{e.event}</span>
                      <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-500/60 rounded-full" style={{ width: `${analytics.by_event[0].count ? Math.round((e.count / analytics.by_event[0].count) * 100) : 0}%` }} />
                      </div>
                      <span className="text-xs font-mono text-slate-400 w-10 text-right">{e.count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">No usage events recorded yet.</p>
              )}
            </div>

            <h2 className="text-xl font-bold text-white mt-12 mb-4">Price points</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="admin-plans">
              {(overview?.plans || []).map((p) => (
                <div key={p.id} data-testid={`admin-plan-${p.id}`} className="bg-[#0d1730] border border-white/10 rounded-2xl p-5">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-white">{p.name}</h3>
                    <span className="font-mono text-emerald-400 text-lg">{p.price === 0 ? "Free" : `$${p.price}`}<span className="text-slate-500 text-xs">{p.interval === "forever" ? "" : `/${p.interval}`}</span></span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">{p.tagline}</p>
                  {p.trial_days && <p className="text-[11px] text-amber-300 mt-1">{p.trial_days}-day free trial</p>}
                  <p className="text-[11px] text-slate-600 mt-2 font-mono">{p.lookup_key || "—"}</p>
                  <ul className="mt-3 space-y-1.5">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-xs text-slate-300"><Check className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" /> {f}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <h2 className="text-xl font-bold text-white mt-12 mb-4">Users & subscriptions ({users.length})</h2>
            <div className="overflow-x-auto rounded-2xl border border-white/10" data-testid="admin-users">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-slate-400 text-left text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Role</th>
                    <th className="px-4 py-3 text-center">Voice</th>
                    <th className="px-4 py-3 text-center">SnapBudget</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {users.map((u) => (
                    <tr key={u.id} data-testid={`admin-user-${u.id}`} className="hover:bg-white/[0.03]">
                      <td className="px-4 py-3 text-slate-200">{u.name}</td>
                      <td className="px-4 py-3 text-slate-400 font-mono text-xs">{u.email}</td>
                      <td className="px-4 py-3">
                        <span className={`text-[11px] px-2 py-0.5 rounded-full ${u.role === "admin" ? "bg-emerald-500/15 text-emerald-300" : "bg-white/5 text-slate-400"}`}>{u.role}</span>
                      </td>
                      <td className="px-4 py-3 text-center">{u.voice_premium ? <Check className="w-4 h-4 text-emerald-400 mx-auto" /> : <X className="w-4 h-4 text-slate-600 mx-auto" />}</td>
                      <td className="px-4 py-3 text-center">
                        {u.snapbudget ? <Check className="w-4 h-4 text-emerald-400 mx-auto" /> : u.snap_trialing ? <span className="text-[11px] text-amber-300">trial</span> : <X className="w-4 h-4 text-slate-600 mx-auto" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : tab === "support" ? (
          <div className="mt-8">
            <SupportInbox />
          </div>
        ) : (
          <BetaProgram />
        )}
      </div>
    </div>
  );
}
